import { X } from 'lucide-react'
import { useState } from 'react'
import { Navigate, Outlet, useParams } from 'react-router-dom'
import { Toaster } from 'sonner'

import logoProdamac from '@/assets/logo-prodamac.webp'
import { getLocalizedPath, getDirection, isLocale, resolvePreferredLocale } from '@/content/i18n'
import { DashboardHeader } from '@/features/dashboard/DashboardHeader'
import { DashboardSidebar } from '@/features/dashboard/DashboardSidebar'
import type { Locale } from '@/types/site'

export function AdminShellLayout() {
  const { locale: localeParam } = useParams<{ locale: string }>()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  if (!isLocale(localeParam)) {
    const fallback = resolvePreferredLocale()
    return <Navigate to={getLocalizedPath(fallback, 'dashboard')} replace />
  }

  const locale = localeParam as Locale
  const direction = getDirection(locale)

  const closeMobile = () => setMobileNavOpen(false)

  const sidebarInner = (
    <>
      <div className="mb-6 flex items-center justify-start px-1">
        <img src={logoProdamac} alt="" className="h-10 w-auto max-w-[160px] object-contain" />
      </div>
      <DashboardSidebar locale={locale} onNavigate={closeMobile} />
    </>
  )

  return (
    <div
      dir={direction}
      lang={locale === 'ar' ? 'ar' : 'en'}
      className="flex h-dvh max-h-dvh w-full flex-row items-stretch overflow-hidden bg-surface"
    >
      <Toaster richColors position={locale === 'ar' ? 'top-left' : 'top-right'} />

      {/* Desktop sidebar */}
      <aside
        dir={direction}
        className="hidden min-h-0 w-64 shrink-0 flex-col overflow-y-auto border-e border-slate-200/80 bg-gradient-to-b from-slate-50 via-white to-brand-light/30 px-3 py-6 shadow-soft [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden lg:flex"
      >
        {sidebarInner}
      </aside>

      {/* Mobile drawer */}
      {mobileNavOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm lg:hidden"
            aria-label="Close menu"
            onClick={closeMobile}
          />
          <div
            dir={direction}
            className="fixed inset-y-0 z-[61] flex w-[min(86vw,22rem)] flex-col overflow-y-auto border-e border-slate-200/80 bg-gradient-to-b from-slate-50 via-white to-brand-light/30 px-3 py-4 shadow-soft [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden start-0 lg:hidden"
          >
            <div className="mb-4 flex items-center justify-between gap-2">
              <img src={logoProdamac} alt="" className="h-9 w-auto max-w-[140px] object-contain" />
              <button
                type="button"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200/90 bg-white text-slate-600 shadow-sm"
                onClick={closeMobile}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <DashboardSidebar locale={locale} onNavigate={closeMobile} />
          </div>
        </>
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-surface">
        <DashboardHeader
          locale={locale}
          direction={direction}
          onOpenMobileNav={() => setMobileNavOpen(true)}
        />

        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-surface p-3 md:p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
